import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id:          string
  email:       string
  fullName:    string
  role:        string
  isWholesale: boolean
}

export interface CartItem {
  id:             string
  variantId:      string
  productName:    string
  variantInfo:    string
  imageUrl?:      string
  unitPrice:      number
  totalPrice:     number
  quantity:       number
  availableStock: number
}

export interface Notification {
  id:      string
  type:    'success' | 'error' | 'info'
  message: string
}

// ---- Cart Store (independiente, sin dependencia circular) ----
interface CartState {
  sessionId:      string
  items:          CartItem[]
  isOpen:         boolean
  addItem:        (item: CartItem) => void
  removeItem:     (id: string) => void
  updateQuantity: (id: string, qty: number) => void
  clearCart:      () => void
  toggleCart:     () => void
  subtotal:       () => number
  itemCount:      () => number
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items:     [],
      isOpen:    false,
      sessionId: crypto.randomUUID(),

      addItem: (item) => {
        const existing = get().items.find(i => i.variantId === item.variantId)
        if (existing) {
          set(s => ({
            items: s.items.map(i =>
              i.variantId === item.variantId
                ? { ...i, quantity: i.quantity + item.quantity,
                         totalPrice: i.unitPrice * (i.quantity + item.quantity) }
                : i
            )
          }))
        } else {
          set(s => ({ items: [...s.items, item] }))
        }
      },

      removeItem: (id) =>
        set(s => ({ items: s.items.filter(i => i.id !== id) })),

      updateQuantity: (id, qty) =>
        set(s => ({
          items: qty <= 0
            ? s.items.filter(i => i.id !== id)
            : s.items.map(i =>
                i.id === id
                  ? { ...i, quantity: qty, totalPrice: i.unitPrice * qty }
                  : i
              )
        })),

      clearCart:  () => set({ items: [], isOpen: false }),
      toggleCart: () => set(s => ({ isOpen: !s.isOpen })),
      subtotal:   () => get().items.reduce((sum, i) => sum + i.totalPrice, 0),
      itemCount:  () => get().items.reduce((sum, i) => sum + i.quantity, 0),
    }),
    {
      name: 'uw-cart',
      // v1: elimina artículos agregados sin talla por el "Agregar" rápido anterior
      // (usaban el ID del producto en lugar del de la variante y el pedido fallaba)
      version: 1,
      migrate: (state: any) => ({
        ...state,
        items: (state?.items ?? []).filter((i: any) => i.variantInfo !== 'Talla única'),
      }),
    }
  )
)

// ---- Auth Store ----
interface AuthState {
  user:    User | null
  token:   string | null
  setAuth: (user: User, token: string) => void
  logout:  () => void
  isAdmin: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user:  null,
      token: null,
      setAuth: (user, token) => {
        // Limpiar carrito del usuario anterior DESPUÉS de guardar nueva sesión
        set({ user, token })
        // Pequeño delay para evitar race condition con persist
        setTimeout(() => {
          useCartStore.getState().clearCart()
        }, 50)
      },
      logout: () => {
        set({ user: null, token: null })
        setTimeout(() => {
          useCartStore.getState().clearCart()
        }, 50)
      },
      isAdmin: () => ['admin', 'superadmin'].includes(get().user?.role ?? ''),
    }),
    { name: 'uw-auth' }
  )
)

// ---- UI Store ----
interface UIState {
  notifications:    Notification[]
  mobileMenuOpen:   boolean
  searchOpen:       boolean
  notify:           (type: Notification['type'], message: string) => void
  dismissNotif:     (id: string) => void
  toggleMobileMenu: () => void
  toggleSearch:     () => void
}

export const useUIStore = create<UIState>()((set, get) => ({
  notifications:  [],
  mobileMenuOpen: false,
  searchOpen:     false,

  notify: (type, message) => {
    const id = Math.random().toString(36).slice(2)
    set(s => ({ notifications: [...s.notifications, { id, type, message }] }))
    setTimeout(() => get().dismissNotif(id), 4000)
  },

  dismissNotif:     (id) =>
    set(s => ({ notifications: s.notifications.filter(n => n.id !== id) })),
  toggleMobileMenu: () => set(s => ({ mobileMenuOpen: !s.mobileMenuOpen })),
  toggleSearch:     () => set(s => ({ searchOpen: !s.searchOpen })),
}))

